import { useClient, useUpdateClient } from '@/api/hooks';
import { ClientLogoEditor } from '@/components/client/ClientLogoEditor';
import { ProfileAvatarEditor } from '@/components/profile/ProfileAvatarEditor';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/ui/PageHeader';
import { PasswordStrength } from '@/components/ui/PasswordStrength';
import { useAuth } from '@/contexts/AuthContext';
import { usePreviewUiLocale } from '@/contexts/LocaleContext';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime, formatMoney, formatWeight } from '@/lib/format';
import { useT } from '@/lib/i18n';
import {
  COUNTRY_OPTIONS,
  DATE_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  TIMEZONE_OPTIONS,
  UI_LOCALE_OPTIONS,
  defaultsForCountry,
  normalizeCountry,
  normalizeCurrency,
  normalizeDateFormat,
  normalizeTimeFormat,
  normalizeTimezone,
  normalizeUiLocale,
  normalizeWeightUnit,
  setLocalePrefs,
  type CountryCode,
  type CurrencyCode,
  type DateFormatPref,
  type TimeFormatPref,
  type UiLocale,
  type WeightUnit,
} from '@/lib/locale';
import { formatPhoneBR, phoneToApi } from '@/lib/masks';
import { canManage, roleLabel } from '@/lib/rbac';
import type { NotificationPreferences } from '@/types/api';
import { Bell, Building2, CreditCard, Globe2, Lock, Shield, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

type TabId = 'perfil' | 'preferencias' | 'notificacoes' | 'senha' | 'privacidade' | 'cliente';

const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  channels: { email: true, push: true, whatsapp: false, sms: false },
  types: {
    sales_lead: true,
    calving_due: true,
    drying_off_due: true,
    slaughter_due: true,
    sync_conflict: true,
  },
};

export function SettingsPage() {
  const { user, refresh } = useAuth();
  const { data: client, refetch: refetchClient } = useClient();
  const updateClient = useUpdateClient();
  const t = useT();
  const previewUiLocale = usePreviewUiLocale();
  const isManager = user ? canManage(user.role) : false;

  const [tab, setTab] = useState<TabId>(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#notificacoes') {
      return 'notificacoes';
    }
    return 'perfil';
  });

  const [profile, setProfile] = useState({ name: '', phone: '', ui_locale: 'pt-BR' as UiLocale });
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const [localeForm, setLocaleForm] = useState({
    country: 'BR' as CountryCode,
    currency: 'BRL' as CurrencyCode,
    weight_unit: 'kg' as WeightUnit,
    date_format: 'dmy' as DateFormatPref,
    time_format: 'h24' as TimeFormatPref,
    timezone: 'America/Sao_Paulo',
  });
  const [localeMsg, setLocaleMsg] = useState('');
  const [localeErr, setLocaleErr] = useState('');

  const [notifyPrefs, setNotifyPrefs] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFS);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyErr, setNotifyErr] = useState('');

  const [mlConsent, setMlConsent] = useState(false);
  const [legalMsg, setLegalMsg] = useState('');
  const [legalErr, setLegalErr] = useState('');
  const [savingLegal, setSavingLegal] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);

  const [clientForm, setClientForm] = useState({
    trade_name: '',
    billing_email: '',
    billing_phone: '',
  });
  const [clientMsg, setClientMsg] = useState('');
  const [clientErr, setClientErr] = useState('');

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name,
        phone: formatPhoneBR(user.phone.replace(/^\+55/, '')),
        ui_locale: normalizeUiLocale(user.ui_locale),
      });
      setNotifyPrefs({
        channels: {
          ...DEFAULT_NOTIFICATION_PREFS.channels,
          ...(user.notification_preferences?.channels ?? {}),
        },
        types: {
          ...DEFAULT_NOTIFICATION_PREFS.types,
          ...(user.notification_preferences?.types ?? {}),
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (!client) return;
    const country = normalizeCountry(client.country);
    setLocaleForm({
      country,
      currency: normalizeCurrency(client.currency, country),
      weight_unit: normalizeWeightUnit(client.weight_unit),
      date_format: normalizeDateFormat(client.date_format),
      time_format: normalizeTimeFormat(client.time_format),
      timezone: normalizeTimezone(client.timezone),
    });
    setClientForm({
      trade_name: client.trade_name ?? '',
      billing_email: client.billing_email ?? '',
      billing_phone: client.billing_phone
        ? formatPhoneBR(client.billing_phone.replace(/^\+55/, ''))
        : '',
    });
    setMlConsent(Boolean(client.ml_consent_at));
  }, [client]);

  // Live preview while editing prefs (does not persist until save).
  useEffect(() => {
    if (tab !== 'preferencias') return;
    previewUiLocale(profile.ui_locale);
    setLocalePrefs({
      country: localeForm.country,
      currency: localeForm.currency,
      weightUnit: localeForm.weight_unit,
      dateFormat: localeForm.date_format,
      timeFormat: localeForm.time_format,
      timezone: localeForm.timezone,
    });
  }, [tab, profile.ui_locale, localeForm, previewUiLocale]);

  const tabs = useMemo(() => {
    const base: { id: TabId; label: string; icon: typeof UserRound }[] = [
      { id: 'perfil', label: t('settings.tab.profile'), icon: UserRound },
      { id: 'preferencias', label: t('settings.tab.locale'), icon: Globe2 },
      { id: 'notificacoes', label: t('settings.tab.notifications'), icon: Bell },
      { id: 'senha', label: t('settings.tab.password'), icon: Lock },
    ];
    if (isManager) {
      base.push({ id: 'privacidade', label: 'Privacidade', icon: Shield });
      base.push({ id: 'cliente', label: t('settings.tab.client'), icon: Building2 });
    }
    return base;
  }, [isManager, t]);

  if (!user) return <Navigate to="/login" replace />;

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileErr('');
    setProfileMsg('');
    setSavingProfile(true);
    try {
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          name: profile.name,
          phone: phoneToApi(profile.phone),
          ui_locale: profile.ui_locale,
        }),
      });
      await refresh();
      setProfileMsg('Perfil atualizado.');
    } catch (err) {
      setProfileErr(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSavingProfile(false);
    }
  }

  async function saveLegalConsent(next: boolean) {
    setLegalErr('');
    setLegalMsg('');
    setSavingLegal(true);
    try {
      await apiFetch('/auth/legal-consent', {
        method: 'PATCH',
        body: JSON.stringify({ ml_consent: next }),
      });
      setMlConsent(next);
      await refetchClient();
      setLegalMsg(next ? 'Consentimento para ML ativado.' : 'Consentimento para ML revogado.');
    } catch (err) {
      setLegalErr(getErrorMessage(err, 'Erro ao atualizar consentimento.'));
    } finally {
      setSavingLegal(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwdErr('');
    setPwdMsg('');
    setSavingPwd(true);
    try {
      await apiFetch('/auth/password/change', {
        method: 'POST',
        body: JSON.stringify(passwords),
      });
      setPwdMsg('Senha alterada com sucesso.');
      setPasswords({ current_password: '', password: '', password_confirmation: '' });
    } catch (err) {
      setPwdErr(getErrorMessage(err, 'Erro ao alterar senha.'));
    } finally {
      setSavingPwd(false);
    }
  }

  async function saveLocale(e: React.FormEvent) {
    e.preventDefault();
    if (!isManager) return;
    setLocaleErr('');
    setLocaleMsg('');
    try {
      await updateClient.mutateAsync({
        country: localeForm.country,
        currency: localeForm.currency,
        weight_unit: localeForm.weight_unit,
        date_format: localeForm.date_format,
        time_format: localeForm.time_format,
        timezone: localeForm.timezone,
      });
      // Also persist personal language if changed from this tab.
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          name: profile.name,
          phone: phoneToApi(profile.phone),
          ui_locale: profile.ui_locale,
        }),
      });
      await refresh();
      setLocaleMsg(t('settings.locale.saved'));
    } catch (err) {
      setLocaleErr(getErrorMessage(err, t('settings.locale.save_error')));
    }
  }

  async function saveLanguageOnly(e: React.FormEvent) {
    e.preventDefault();
    setLocaleErr('');
    setLocaleMsg('');
    try {
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          name: profile.name,
          phone: phoneToApi(profile.phone),
          ui_locale: profile.ui_locale,
        }),
      });
      await refresh();
      setLocaleMsg(t('settings.locale.language_saved'));
    } catch (err) {
      setLocaleErr(getErrorMessage(err, t('settings.locale.save_error')));
    }
  }

  async function saveNotifications(e: React.FormEvent) {
    e.preventDefault();
    setNotifyErr('');
    setNotifyMsg('');
    setSavingNotify(true);
    try {
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          notification_preferences: notifyPrefs,
        }),
      });
      await refresh();
      setNotifyMsg('Preferências de notificação salvas.');
    } catch (err) {
      setNotifyErr(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSavingNotify(false);
    }
  }

  async function saveClient(e: React.FormEvent) {
    e.preventDefault();
    setClientErr('');
    setClientMsg('');
    try {
      await updateClient.mutateAsync({
        trade_name: clientForm.trade_name,
        billing_email: clientForm.billing_email,
        billing_phone: clientForm.billing_phone
          ? phoneToApi(clientForm.billing_phone)
          : null,
      });
      setClientMsg('Dados do cliente atualizados.');
    } catch (err) {
      setClientErr(getErrorMessage(err, 'Erro ao salvar.'));
    }
  }

  function applyCountryDefaults(country: CountryCode) {
    const defaults = defaultsForCountry(country);
    setLocaleForm((f) => ({
      ...f,
      country,
      currency: defaults.currency,
      weight_unit: defaults.weightUnit,
      date_format: defaults.dateFormat,
      time_format: defaults.timeFormat,
      timezone: defaults.timezone,
    }));
  }

  const previewNow = new Date().toISOString();

  return (
    <div>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      <div className="mb-6 flex flex-wrap gap-1 rounded-2xl border border-manejai-sand bg-manejai-cream/30 p-1 max-w-3xl">
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setTab(item.id);
                if (item.id === 'notificacoes') {
                  window.history.replaceState(null, '', '#notificacoes');
                } else if (window.location.hash) {
                  window.history.replaceState(null, '', window.location.pathname + window.location.search);
                }
              }}
              className={cn(
                'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition',
                active
                  ? 'bg-white text-manejai-forest shadow-sm'
                  : 'text-manejai-bark/70 hover:text-manejai-forest',
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 max-w-2xl">
        {tab === 'perfil' && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">Seu perfil</h2>
            </CardHeader>
            <CardBody className="space-y-6">
              <ProfileAvatarEditor user={user} onUpdated={refresh} />
              <form onSubmit={saveProfile} className="space-y-4 border-t border-manejai-sand/60 pt-6">
                {profileMsg && <Alert variant="success">{profileMsg}</Alert>}
                {profileErr && <Alert variant="error">{profileErr}</Alert>}
                <Input
                  label="Nome"
                  required
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
                <Input label="E-mail" value={user.email} disabled hint="Alteração de e-mail em breve" />
                <Input
                  label="Telefone"
                  required
                  inputMode="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: formatPhoneBR(e.target.value) })}
                />
                <Row label="Perfil" value={roleLabel(user.role)} />
                <Button type="submit" loading={savingProfile}>
                  Salvar perfil
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        {tab === 'preferencias' && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">{t('settings.locale.title')}</h2>
            </CardHeader>
            <CardBody>
              <form
                onSubmit={isManager ? saveLocale : saveLanguageOnly}
                className="space-y-5"
              >
                {localeMsg && <Alert variant="success">{localeMsg}</Alert>}
                {localeErr && <Alert variant="error">{localeErr}</Alert>}

                <Select
                  label={t('settings.locale.language')}
                  value={profile.ui_locale}
                  onChange={(e) => {
                    const locale = normalizeUiLocale(e.target.value);
                    setProfile({ ...profile, ui_locale: locale });
                    previewUiLocale(locale);
                  }}
                  options={UI_LOCALE_OPTIONS}
                />
                <p className="text-xs text-manejai-bark/55 -mt-3">
                  {t('settings.locale.language_hint')}
                </p>

                <div className="border-t border-manejai-sand/60 pt-5 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-manejai-forest">
                      {t('settings.locale.formats_title')}
                    </p>
                    <p className="mt-1 text-xs text-manejai-bark/55">
                      {t('settings.locale.formats_hint')}
                    </p>
                  </div>

                  <Select
                    label={t('settings.locale.country')}
                    value={localeForm.country}
                    disabled={!isManager}
                    onChange={(e) => applyCountryDefaults(normalizeCountry(e.target.value))}
                    options={COUNTRY_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
                  />
                  <Select
                    label={t('settings.locale.currency')}
                    value={localeForm.currency}
                    disabled={!isManager}
                    onChange={(e) =>
                      setLocaleForm({
                        ...localeForm,
                        currency: normalizeCurrency(e.target.value, localeForm.country),
                      })
                    }
                    options={COUNTRY_OPTIONS.map((c) => ({
                      value: c.currency,
                      label: `${c.currency} (${c.label})`,
                    })).filter(
                      (opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i,
                    )}
                  />
                  <Select
                    label={t('settings.locale.weight_unit')}
                    value={localeForm.weight_unit}
                    disabled={!isManager}
                    onChange={(e) =>
                      setLocaleForm({
                        ...localeForm,
                        weight_unit: normalizeWeightUnit(e.target.value),
                      })
                    }
                    options={[
                      { value: 'kg', label: t('locale.weight.kg') },
                      { value: 'lb', label: t('locale.weight.lb') },
                    ]}
                  />
                  <Select
                    label={t('settings.locale.date_format')}
                    value={localeForm.date_format}
                    disabled={!isManager}
                    onChange={(e) =>
                      setLocaleForm({
                        ...localeForm,
                        date_format: normalizeDateFormat(e.target.value),
                      })
                    }
                    options={DATE_FORMAT_OPTIONS.map((o) => {
                      const labelKey =
                        o.value === 'mdy'
                          ? 'locale.date.mdy'
                          : o.value === 'ymd'
                            ? 'locale.date.ymd'
                            : 'locale.date.dmy';
                      return {
                        value: o.value,
                        label: `${t(labelKey)} — ${o.example}`,
                      };
                    })}
                  />
                  <Select
                    label={t('settings.locale.time_format')}
                    value={localeForm.time_format}
                    disabled={!isManager}
                    onChange={(e) =>
                      setLocaleForm({
                        ...localeForm,
                        time_format: normalizeTimeFormat(e.target.value),
                      })
                    }
                    options={TIME_FORMAT_OPTIONS.map((o) => ({
                      value: o.value,
                      label: `${t(o.value === 'h12' ? 'locale.time.h12' : 'locale.time.h24')} — ${o.example}`,
                    }))}
                  />
                  <Select
                    label={t('settings.locale.timezone')}
                    value={localeForm.timezone}
                    disabled={!isManager}
                    onChange={(e) =>
                      setLocaleForm({
                        ...localeForm,
                        timezone: normalizeTimezone(e.target.value),
                      })
                    }
                    options={TIMEZONE_OPTIONS}
                  />

                  {!isManager && (
                    <p className="text-xs text-manejai-bark/55">
                      {t('settings.locale.formats_readonly')}
                    </p>
                  )}

                  <div className="rounded-xl border border-manejai-sand bg-manejai-cream/40 px-4 py-3 text-sm text-manejai-bark/80 space-y-1">
                    <p className="font-medium text-manejai-forest">Prévia</p>
                    <p>Data: {formatDate(previewNow.slice(0, 10))}</p>
                    <p>Data e hora: {formatDateTime(previewNow)}</p>
                    <p>Peso: {formatWeight(387.5)}</p>
                    <p>Moeda: {formatMoney(1250.5)}</p>
                  </div>
                </div>

                <Button type="submit" loading={updateClient.isPending || savingProfile}>
                  {isManager ? t('settings.locale.save') : t('settings.locale.save_language')}
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        {tab === 'notificacoes' && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">Notificações</h2>
            </CardHeader>
            <CardBody>
              <form onSubmit={saveNotifications} className="space-y-6">
                {notifyMsg && <Alert variant="success">{notifyMsg}</Alert>}
                {notifyErr && <Alert variant="error">{notifyErr}</Alert>}

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-manejai-forest">Canais</p>
                    <p className="mt-1 text-xs text-manejai-bark/55">
                      Como você prefere receber avisos. WhatsApp e SMS entram em breve — a preferência
                      já fica salva.
                    </p>
                  </div>
                  <PrefToggle
                    label="E-mail"
                    hint="Avisos no e-mail cadastrado"
                    checked={notifyPrefs.channels.email}
                    onChange={(email) =>
                      setNotifyPrefs((p) => ({ ...p, channels: { ...p.channels, email } }))
                    }
                  />
                  <PrefToggle
                    label="Push / app"
                    hint="Inbox no sistema (e push nativo nos apps em breve)"
                    checked={notifyPrefs.channels.push}
                    onChange={(push) =>
                      setNotifyPrefs((p) => ({ ...p, channels: { ...p.channels, push } }))
                    }
                  />
                  <PrefToggle
                    label="WhatsApp"
                    hint="Em breve"
                    checked={notifyPrefs.channels.whatsapp}
                    disabled
                    onChange={(whatsapp) =>
                      setNotifyPrefs((p) => ({ ...p, channels: { ...p.channels, whatsapp } }))
                    }
                  />
                  <PrefToggle
                    label="SMS"
                    hint="Em breve"
                    checked={notifyPrefs.channels.sms}
                    disabled
                    onChange={(sms) =>
                      setNotifyPrefs((p) => ({ ...p, channels: { ...p.channels, sms } }))
                    }
                  />
                </div>

                <div className="space-y-3 border-t border-manejai-sand/60 pt-5">
                  <div>
                    <p className="text-sm font-medium text-manejai-forest">O que receber</p>
                    <p className="mt-1 text-xs text-manejai-bark/55">
                      Lembretes do dia rodam automaticamente por volta das 05:00 no fuso da conta.
                    </p>
                  </div>
                  <PrefToggle
                    label="Novos leads de venda"
                    hint="Quando alguém demonstra interesse na página pública"
                    checked={notifyPrefs.types.sales_lead}
                    onChange={(sales_lead) =>
                      setNotifyPrefs((p) => ({ ...p, types: { ...p.types, sales_lead } }))
                    }
                  />
                  <PrefToggle
                    label="Parto previsto (hoje)"
                    hint="Vacas com expected calving na data de hoje"
                    checked={notifyPrefs.types.calving_due}
                    onChange={(calving_due) =>
                      setNotifyPrefs((p) => ({ ...p, types: { ...p.types, calving_due } }))
                    }
                  />
                  <PrefToggle
                    label="Secagem prevista (hoje)"
                    checked={notifyPrefs.types.drying_off_due}
                    onChange={(drying_off_due) =>
                      setNotifyPrefs((p) => ({ ...p, types: { ...p.types, drying_off_due } }))
                    }
                  />
                  <PrefToggle
                    label="Abate previsto (hoje)"
                    hint="Lotes de venda com slaughter_date hoje"
                    checked={notifyPrefs.types.slaughter_due}
                    onChange={(slaughter_due) =>
                      setNotifyPrefs((p) => ({ ...p, types: { ...p.types, slaughter_due } }))
                    }
                  />
                  <PrefToggle
                    label="Conflitos de sincronização"
                    hint="Quando o app offline precisa de resolução"
                    checked={notifyPrefs.types.sync_conflict}
                    onChange={(sync_conflict) =>
                      setNotifyPrefs((p) => ({ ...p, types: { ...p.types, sync_conflict } }))
                    }
                  />
                </div>

                <Button type="submit" loading={savingNotify}>
                  Salvar notificações
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        {tab === 'senha' && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">Alterar senha</h2>
            </CardHeader>
            <CardBody>
              <form onSubmit={savePassword} className="space-y-4">
                {pwdMsg && <Alert variant="success">{pwdMsg}</Alert>}
                {pwdErr && <Alert variant="error">{pwdErr}</Alert>}
                <Input
                  label="Senha atual"
                  type="password"
                  required
                  value={passwords.current_password}
                  onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                />
                <Input
                  label="Nova senha"
                  type="password"
                  required
                  value={passwords.password}
                  onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                />
                <PasswordStrength password={passwords.password} />
                <Input
                  label="Confirmar nova senha"
                  type="password"
                  required
                  value={passwords.password_confirmation}
                  onChange={(e) =>
                    setPasswords({ ...passwords, password_confirmation: e.target.value })
                  }
                />
                <Button type="submit" loading={savingPwd}>
                  Alterar senha
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        {tab === 'privacidade' && isManager && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">Privacidade e consentimentos</h2>
            </CardHeader>
            <CardBody className="space-y-4">
              {legalMsg && <Alert variant="success">{legalMsg}</Alert>}
              {legalErr && <Alert variant="error">{legalErr}</Alert>}
              <div className="text-sm text-manejai-bark/75 space-y-2">
                <p>
                  Termos aceitos:{' '}
                  {user.terms_accepted_at
                    ? `${formatDateTime(user.terms_accepted_at)} (v${user.terms_version ?? '—'})`
                    : '—'}
                </p>
                <p>
                  <Link to="/termos" className="text-manejai-moss hover:underline" target="_blank" rel="noopener noreferrer">
                    Termos de Uso
                  </Link>
                  {' · '}
                  <Link to="/privacidade" className="text-manejai-moss hover:underline" target="_blank" rel="noopener noreferrer">
                    Política de Privacidade
                  </Link>
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer border-t border-manejai-sand/60 pt-4">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-manejai-sand text-manejai-forest"
                  checked={mlConsent}
                  disabled={savingLegal}
                  onChange={(e) => void saveLegalConsent(e.target.checked)}
                />
                <span className="text-sm text-manejai-bark/85 leading-relaxed">
                  Autorizo o uso de fotos e vídeos capturados na plataforma para melhorar a
                  identificação por imagem (treino de modelos e active learning). Revogar interrompe
                  novos envios ao cofre de treino ML.
                </span>
              </label>
              {client?.ml_consent_at && (
                <p className="text-xs text-manejai-bark/55">
                  Consentimento ML desde {formatDateTime(client.ml_consent_at)}
                  {client.ml_consent_version ? ` (v${client.ml_consent_version})` : ''}
                </p>
              )}
            </CardBody>
          </Card>
        )}

        {tab === 'cliente' && client && isManager && (
          <Card>
            <CardHeader>
              <h2 className="font-medium text-manejai-forest">Dados da conta</h2>
            </CardHeader>
            <CardBody className="space-y-6">
              <ClientLogoEditor client={client} />
              <form onSubmit={saveClient} className="space-y-4 border-t border-manejai-sand/60 pt-6">
                {clientMsg && <Alert variant="success">{clientMsg}</Alert>}
                {clientErr && <Alert variant="error">{clientErr}</Alert>}
                <Row label="Razão social" value={client.legal_name} />
                <Row label="Documento" value={client.document} />
                <Input
                  label="Nome fantasia"
                  value={clientForm.trade_name}
                  onChange={(e) => setClientForm({ ...clientForm, trade_name: e.target.value })}
                />
                <Input
                  label="E-mail de cobrança"
                  type="email"
                  value={clientForm.billing_email}
                  onChange={(e) => setClientForm({ ...clientForm, billing_email: e.target.value })}
                />
                <Input
                  label="Telefone da fazenda"
                  inputMode="tel"
                  hint="Aparece na página pública de vendas"
                  value={clientForm.billing_phone}
                  onChange={(e) =>
                    setClientForm({
                      ...clientForm,
                      billing_phone: formatPhoneBR(e.target.value),
                    })
                  }
                />
                <Button type="submit" loading={updateClient.isPending}>
                  Salvar dados
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-manejai-forest">Plano e pagamento</p>
              <p className="text-sm text-manejai-bark/60 mt-0.5">
                Plano atual: <strong>{client?.plan_code ?? '—'}</strong>
              </p>
            </div>
            <Link to="/assinatura">
              <Button variant="outline">
                <CreditCard className="h-4 w-4" />
                Ver planos
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-manejai-bark/60">{label}</span>
      <span className="font-medium text-manejai-forest text-right">{value}</span>
    </div>
  );
}

function PrefToggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-xl border border-manejai-sand bg-white px-3 py-2.5',
        disabled && 'opacity-60',
      )}
    >
      <input
        type="checkbox"
        className="mt-1 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-sage/40"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-manejai-forest">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-manejai-bark/55">{hint}</span>}
      </span>
    </label>
  );
}
